export type CoordenadasGeolocalizacao = {
  latitude: number;
  longitude: number;
  accuracy?: number;
};

const PRECISAO_MAXIMA_METROS = 100;
const TIMEOUT_MS = 15000;
const TENTATIVAS_ALTA_PRECISAO = 3;
const TENTATIVAS_BAIXA_PRECISAO = 2;
const INTERVALO_ENTRE_TENTATIVAS_MS = 1500;

type BuscaCoordenadasOpcoes = {
  altaPrecisao?: boolean;
  tentativas?: number;
};

function aguardar(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function coordenadaTemValor(latitude: number, longitude: number): boolean {
  return !!latitude && !!longitude;
}

function coordenadaPrecisa(coordenadas: CoordenadasGeolocalizacao): boolean {
  if (!coordenadaTemValor(coordenadas.latitude, coordenadas.longitude)) {
    return false;
  }

  if (coordenadas.accuracy == null) {
    return true;
  }

  return coordenadas.accuracy <= PRECISAO_MAXIMA_METROS;
}

function melhorCoordenada(
  atual: CoordenadasGeolocalizacao,
  candidata: CoordenadasGeolocalizacao
): CoordenadasGeolocalizacao {
  if (!coordenadaTemValor(candidata.latitude, candidata.longitude)) {
    return atual;
  }

  if (!coordenadaTemValor(atual.latitude, atual.longitude)) {
    return candidata;
  }

  if (atual.accuracy == null) {
    return candidata;
  }

  if (candidata.accuracy == null) {
    return atual;
  }

  return candidata.accuracy < atual.accuracy ? candidata : atual;
}

function buscaCoordenadaUnica(opcoes: PositionOptions): Promise<CoordenadasGeolocalizacao> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({
        latitude: 0,
        longitude: 0,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      () => {
        resolve({
          latitude: 0,
          longitude: 0,
        });
      },
      opcoes
    );
  });
}

async function buscaComTentativas(
  altaPrecisao: boolean,
  tentativas: number
): Promise<CoordenadasGeolocalizacao> {
  const positionOptions: PositionOptions = {
    enableHighAccuracy: altaPrecisao,
    maximumAge: 0,
    timeout: TIMEOUT_MS,
  };

  let melhor: CoordenadasGeolocalizacao = { latitude: 0, longitude: 0 };

  for (let i = 0; i < tentativas; i++) {
    if (i > 0) {
      await aguardar(INTERVALO_ENTRE_TENTATIVAS_MS);
    }

    const resultado = await buscaCoordenadaUnica(positionOptions);
    melhor = melhorCoordenada(melhor, resultado);

    if (coordenadaPrecisa(melhor)) {
      break;
    }

    if (!altaPrecisao && coordenadaTemValor(melhor.latitude, melhor.longitude)) {
      break;
    }
  }

  return melhor;
}

export const buscaCoordenadasAtuais = async (
  opcoes: BuscaCoordenadasOpcoes = {}
): Promise<CoordenadasGeolocalizacao> => {
  const altaPrecisao = opcoes.altaPrecisao != false;
  const tentativas = opcoes.tentativas ?? (altaPrecisao ? TENTATIVAS_ALTA_PRECISAO : TENTATIVAS_BAIXA_PRECISAO);

  if (!altaPrecisao) {
    return buscaComTentativas(false, tentativas);
  }

  let melhor = await buscaComTentativas(true, tentativas);

  if (coordenadaPrecisa(melhor)) {
    return melhor;
  }

  if (coordenadaTemValor(melhor.latitude, melhor.longitude)) {
    return melhor;
  }

  const fallback = await buscaComTentativas(false, TENTATIVAS_BAIXA_PRECISAO);
  return melhorCoordenada(melhor, fallback);
};

export const buscaCoordenadasParaEntrega = async (
  coordenadasChegada?: { latitude?: string | number; longitude?: string | number } | null
): Promise<CoordenadasGeolocalizacao | null> => {
  const atual = await buscaCoordenadasAtuais({
    altaPrecisao: true,
    tentativas: TENTATIVAS_ALTA_PRECISAO,
  });

  if (coordenadaPrecisa(atual)) {
    return atual;
  }

  const chegadaLat = Number(coordenadasChegada?.latitude) || 0;
  const chegadaLng = Number(coordenadasChegada?.longitude) || 0;

  if (coordenadaTemValor(chegadaLat, chegadaLng)) {
    return {
      latitude: chegadaLat,
      longitude: chegadaLng,
    };
  }

  if (coordenadaTemValor(atual.latitude, atual.longitude)) {
    return atual;
  }

  return null;
};
